import { useEffect, useState } from "react";
import { Link, generatePath, useNavigate, useParams } from "react-router-dom";
import EntityTypes from "../../models/entityTypes";
import Project from "../../models/project";
import { api } from "../../store/api";
import { useAppSelector } from "../../store/hooks";
import { selectLabels } from "../../store/labelsSlice";
import { PartnerDetailsRoute } from "../partners/partnerDetailsScreen";
import RecordDetailsCard from "../../components/records/recordDetailsCard";
import KnowledgeMapTypes from "../../models/knowledgeMapTypes";
import { setTitle } from "../../util/useDocumentTitle";
import { usePermissions } from "../../util/usePermissions";
import { ProjectDetailsRoute } from "./projectDetailsScreen";
import FileBlob from "../../models/fileBlob";
import { FormProvider, SubmitHandler, useForm, useWatch } from "react-hook-form";
import { ProjectCloneRequest } from "../../models/projectCloneRequest";
import { PartnerDropdown } from "../../components/partner/partnerDropdown";


export const ProjectCloneRoute = "/projects/:id/clone";

interface ProjectCloneRouteParams {
  id?: string;
}

export default function ProjectCloneScreen() {
  const navigate = useNavigate();
  const labels = useAppSelector(selectLabels);

  const [isLoading, setIsLoading] = useState(false);
  const [isCloning, setIsCloning] = useState(false);
  const [record, setRecord] = useState<Project>();
  const [error, setError] = useState<String>();

  const form = useForm<ProjectCloneRequest>();
  const { register, handleSubmit, reset, formState: { errors }, control } = form;

  const onSubmit: SubmitHandler<ProjectCloneRequest> = data => {
    console.log(data);
    // const request = {};
    if (record?.id) {
      setIsCloning(true);
      api.project(record?.id).clone(data)
        .then((result) => {
          setIsCloning(false);
          navigate(generatePath(ProjectDetailsRoute, { id: result.id?.toString() ?? 'ERR' }));
        })
        .catch((reason) => {
          console.error(reason);
          setError("Unable to clone " + labels.project.singular + ` #${params.id}: ${reason}`);
          setIsCloning(false);
        });
    }
  };

  const watchCloneTags = useWatch({
    control: control,
    name: 'cloneTags',
  });

  const watchCloneGradeUnits = useWatch({
    control: control,
    name: 'cloneGradeUnits',
  });

  //cloneProjectResources
  const watchCloneProjectResources = useWatch({
    control: control,
    name: 'cloneProjectResources',
  });

  //cloneReviewerAssignments
  const watchCloneReviewerAssignments = useWatch({
    control: control,
    name: 'cloneReviewerAssignments',
  });
  //linkReviews
  const watchLinkReviews = useWatch({
    control: control,
    name: 'linkReviews',
  });
  //duplicateReviews
  const watchDuplicateReviews = useWatch({
    control: control,
    name: 'duplicateReviews',
  });

  const { hasPermission } = usePermissions();

  const params = useParams<keyof ProjectCloneRouteParams>();
  useEffect(() => {
    // Load the attribute and set the form with the current values
    setIsLoading(true);
    api.projects.get(parseInt(params.id!))
      .then((record) => {
        setRecord(record);
        setTitle([`${labels.project.singular} Clone`, record.name!]);
        setIsLoading(false);
      })
      .catch((reason) => {
        console.error(reason);
        setError("Unable to load " + labels.project.singular + ` #${params.id}`);
      });
  }, [labels.project.singular, params.id]);

  // useEffect(() => {
  //   if (record?.id) {
  //     api.project(record.id).share().then((response) => {
  //       setShareUrl(`${window.location.protocol}://${window.location.host}/share/${encodeURIComponent(response.token)}/project`);
  //       setIsLoading(false);
  //     });
  //   }
  //   else {
  //     setShareUrl(undefined);
  //   }
  // }, [record]);

  return (<div>
    <div className="d-flex align-items-center mb-3">
      <div>
        <h1 className="page-header mb-0">Clone {labels.project.singular}</h1>
        <ul className="breadcrumb">
          <li className="breadcrumb-item"><Link to={generatePath(PartnerDetailsRoute, { id: `${record?.partner?.id}` })}>{record?.partner?.name}</Link></li>
          <li className="breadcrumb-item"><Link to={generatePath(ProjectDetailsRoute, { id: `${record?.id}` })}>{record?.name}</Link></li>
          <li className="breadcrumb-item active">Clone {labels.project.singular}</li>
        </ul>
      </div>
    </div>
    {error && <div className="alert alert-danger"><strong>Error!</strong> {error}</div>}
    <div className="row">
      <div className="col-md-4">
        {record && <RecordDetailsCard
          record={record}
          type={EntityTypes.Project}
          // editRoute={ProjectEditRoute}
          // footer={<>
          //   <RequiresPermission permission={PermissionTypes.ProjectEdit}>
          //     <Link to={generatePath(ProjectResourcesRoute, { projectId: `${record?.id}` })} className="ms-auto text-decoration-none text-gray-500"><i className="fa fa-edit fa-lg me-2 ms-n2"></i> Add</Link>
          //   </RequiresPermission>
          // </>}
          displayAttributes={[
            { label: labels.partner.singular, callback: (record: Project) => <Link to={generatePath(PartnerDetailsRoute, { id: `${record?.partnerId}` })}>{record?.partner?.name}</Link>, },
            { label: 'Knowledge Map Type', callback: (record: Project) => <>{labels.knowledgeMapTypes[record.knowledgeMapType ?? KnowledgeMapTypes.Undefined]}</>, },
            { label: labels.unitevaluation.plural, callback: (record: Project) => <>{record.unitEvaluations === true ? 'Yes' : 'No'}</>, },
          ]}
        />}
      </div>
      <div className="col-md-8">
        <div className="card border-0 mb-4">
          <FormProvider {...form}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="card-body">
                <p>
                  Create a new {labels.project.singular} by copying the attributes and related information according to the form below:
                </p>
                {isLoading && <p><i className="fa-solid fa-spin fa-spinner"></i> Loading...</p>}

                <div className="row mb-15px">
                  <label className="form-label col-form-label col-md-3">Partner</label>
                  <div className="col-md-9">
                    <PartnerDropdown />
                    {errors.partner && <div className="invalid-feedback">This field is required</div>}
                  </div>
                </div>

                <div className="row mb-15px">
                  <label className="form-label col-form-label col-md-3">New {labels.project.singular} Name</label>
                  <div className="col-md-9">
                    <input type="text" autoComplete="off" className={"form-control mb-5px " + (errors.name ? 'is-invalid' : '')} {...register("name", { required: true })} />
                    {errors.name && <div className="invalid-feedback">This field is required</div>}
                  </div>
                </div>
                <div className="row mb-15px">
                  <label className="form-label col-form-label col-md-3">Clone {labels.project.singular} Tags?</label>
                  <div className="col-md-3 pt-2">
                    <div className="form-check form-switch mb-2">
                      <input className="form-check-input" type="checkbox" id="cloneTags" {...register("cloneTags")} />
                      <label className="form-check-label" htmlFor="cloneTags">{Boolean(watchCloneTags) ? 'Yes' : 'No'}</label>
                    </div>
                  </div>
                  <label className="form-label col-form-label col-md-3">Clone {labels.project.singular} Grade Units?</label>
                  <div className="col-md-3 pt-2">
                    <div className="form-check form-switch mb-2">
                      <input className="form-check-input" type="checkbox" id="cloneGradeUnits" {...register("cloneGradeUnits")} />
                      <label className="form-check-label" htmlFor="cloneGradeUnits">{Boolean(watchCloneGradeUnits) ? 'Yes' : 'No'}</label>
                    </div>
                  </div>
                  <label className="form-label col-form-label col-md-3">Clone {labels.projectResource.plural}?</label>
                  <div className="col-md-3 pt-2">
                    {Boolean(watchCloneGradeUnits) ? <div className="form-check form-switch mb-2">
                      <input className="form-check-input" type="checkbox" id="cloneProjectResources" {...register("cloneProjectResources")} />
                      <label className="form-check-label" htmlFor="cloneProjectResources">{Boolean(watchCloneProjectResources) ? 'Yes' : 'No'}</label>
                    </div> : <small>Must clone Grade Units</small>}
                  </div>
                  <label className="form-label col-form-label col-md-3">Clone {labels.reviewer.singular} Assignments?</label>
                  <div className="col-md-3 pt-2">
                    {Boolean(watchCloneGradeUnits) ? <div className="form-check form-switch mb-2">
                      <input className="form-check-input" type="checkbox" id="cloneReviewerAssignments" {...register("cloneReviewerAssignments")} />
                      <label className="form-check-label" htmlFor="cloneReviewerAssignments">{Boolean(watchCloneReviewerAssignments) ? 'Yes' : 'No'}</label>
                    </div> : <small>Must clone Grade Units</small>}
                  </div>

                  <label className="form-label col-form-label col-md-3">Duplicate {labels.review.plural}?</label>
                  <div className="col-md-3 pt-2">
                    {Boolean(watchCloneGradeUnits) && Boolean(watchCloneReviewerAssignments) && Boolean(watchCloneProjectResources) ? <div className="form-check form-switch mb-2">
                      <input className="form-check-input" type="checkbox" id="duplicateReviews" {...register("duplicateReviews")} />
                      <label className="form-check-label" htmlFor="duplicateReviews">{Boolean(watchDuplicateReviews) ? 'Yes' : 'No'}</label>
                    </div> : <small>Must clone Grade Units, {labels.projectResource.plural}, and {labels.reviewer.singular} Assignments</small>}
                  </div>

                </div>
              </div>
              <div className="card-footer">
                <div className="text-center">
                  {isCloning === false && <button type="submit" className="btn btn-primary w-100px me-5px">Clone {labels.project.singular}</button>}
                  {isCloning === true && <button type="submit" className="btn btn-primary w-100px me-5px" disabled={true}><i className="fa-solid fa-spin fa-spinner"></i> Cloning {labels.project.singular}...</button>}
                  <button type="button" onClick={() => navigate(-1)} className="btn btn-default w-100px">Cancel</button>
                </div>
              </div>

            </form>
          </FormProvider>
        </div>
      </div>
    </div>
  </div>);
}
